'use client'

import { useRef, useState } from 'react'

import styles from './GiftCardForm.module.scss'
import useI18n from '@/hooks/use-i18n'
import useCurrentPage from '@/hooks/use-current-page'
import SanityImage from '@/components/SanityImage/SanityImage'
import FormField, { FormFieldChangeEvent, FormFieldProps } from '@/components/FormField/FormField'
import Button from '@/components/Button/Button'
import { isValidEmail } from '@/utils/strings/email'

const IS_CLASSIC_GIFT_CARD = true

export type CardType = 'digital' | 'physical'
export type CardMetadata = {
  id: string
  cardType: CardType
  faceplateCode: string
  image: SanityImage
}
export type CardCategory = {
  id: string
  title: string
  cards: CardMetadata[]
}
export type GiftCardFormProps = {
  cardType: CardType
  faceplateCode: string
  image: SanityImage
  onSubmit?: () => void
}

export type GiftCardFormField =
  | 'selectedAmount'
  | 'senderEmail'
  | 'message'
  | 'recipientEmail'
  | 'recipientName'
  | 'recipientType'
  | 'senderName'

const GiftCardForm = ({ cardType, image, faceplateCode, onSubmit }: GiftCardFormProps) => {
  const formRef = useRef<HTMLFormElement>(null)
  const { i18n } = useI18n()
  const { currentLanguage } = useCurrentPage()

  const validateField = (fieldName: GiftCardFormField, value: string): string | undefined => {
    const field = formFields[fieldName]

    // Check if required field is empty
    if (field.required && !value.trim()) {
      return field.errorMessage
    }

    // Email validation for email fields
    if ((fieldName === 'recipientEmail' || fieldName === 'senderEmail') && value.trim()) {
      if (!isValidEmail(value)) {
        return i18n('invalidEmailAddress')
      }
    }

    return undefined
  }

  const handleBlur = (fieldName: GiftCardFormField) => (event: FormFieldChangeEvent) => {
    const { value } = (event?.target as HTMLInputElement) || { value: '' }
    const errorMessage = validateField(fieldName, value)

    if (errorMessage) {
      setValidationErrors(prevState => {
        const filtered = prevState.filter(error => error.field !== fieldName)
        return [...filtered, { field: fieldName, errorMessage }]
      })
    } else {
      setValidationErrors(prevState => prevState.filter(error => error.field !== fieldName))
    }
  }

  const handleChange = (fieldName: GiftCardFormField) => (event: FormFieldChangeEvent) => {
    setValidationErrors(prevState => prevState.filter(error => error.field !== fieldName))

    const { value } = (event?.target as HTMLInputElement) || { value: '' }
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: value,
    }))
  }

  const formFields: Record<GiftCardFormField, FormFieldProps> = {
    selectedAmount: {
      name: 'selectedAmount',
      type: 'amount',
      required: true,
      value: '$25',
      onChange: handleChange('selectedAmount'),
      onBlur: handleBlur('selectedAmount'),
      errorMessage: i18n('requiredField'),
      className: styles.field,
      items: ['25', '50', '100', '150', '250'],
      min: '25',
      max: '500',
    },
    recipientType: {
      name: 'recipientType',
      type: 'toggle',
      required: true,
      value: i18n('someoneElse'),
      onChange: handleChange('recipientType'),
      onBlur: handleBlur('recipientType'),
      errorMessage: i18n('requiredField'),
      className: styles.field,
      items: [i18n('someoneElse'), i18n('myself')],
    },
    recipientName: {
      name: 'recipientName',
      type: 'text',
      value: '',
      required: true,
      onChange: handleChange('recipientName'),
      onBlur: handleBlur('recipientName'),
      placeholder: `${i18n('recipientName')} *`,
      errorMessage: i18n('requiredField'),
      className: styles.field,
      maxLength: 30,
    },
    recipientEmail: {
      name: 'recipientEmail',
      type: 'text',
      value: '',
      required: true,
      onChange: handleChange('recipientEmail'),
      onBlur: handleBlur('recipientEmail'),
      placeholder: `${i18n('recipientEmail')} *`,
      errorMessage: i18n('invalidEmailAddress'),
      className: styles.field,
    },
    senderName: {
      name: 'senderName',
      type: 'text',
      value: '',
      required: true,
      onChange: handleChange('senderName'),
      onBlur: handleBlur('senderName'),
      placeholder: `${i18n('yourName')} *`,
      errorMessage: i18n('requiredField'),
      className: styles.field,
      maxLength: 30,
    },
    senderEmail: {
      name: 'senderEmail',
      type: 'text',
      value: '',
      required: true,
      onChange: handleChange('senderEmail'),
      onBlur: handleBlur('senderEmail'),
      placeholder: `${i18n('yourEmail')} *`,
      errorMessage: i18n('invalidEmailAddress'),
      className: styles.field,
      autoComplete: 'email',
    },
    message: {
      name: 'message',
      type: 'textarea',
      value: '',
      onChange: handleChange('message'),
      onBlur: handleBlur('message'),
      placeholder: i18n('messageOptional'),
      className: styles.field,
    },
  } as const
  const intialState = Object.entries(formFields).reduce(
    (acc, [fieldName, fieldMetadata]) => ({ ...acc, [fieldName]: fieldMetadata.value }),
    {} as { -readonly [key in keyof typeof formFields]: string },
  )

  const [formData, setFormData] = useState(intialState)
  const [validationErrors, setValidationErrors] = useState<{ field: string; errorMessage: string }[]>([])

  const getFormFieldProps = (fieldName: keyof typeof formFields): FormFieldProps => {
    return {
      ...formFields[fieldName],
      value: formData[fieldName],
      errorMessage: validationErrors.find(error => error.field === fieldName)?.errorMessage,
    }
  }

  const isItForSomeoneElse = formData.recipientType === i18n('someoneElse')
  const isItForMyself = formData.recipientType === i18n('myself')

  // Determine which CashStar URL to use based on region
  const isCanada = currentLanguage?.includes('-ca')
  const cashStarUrl = isCanada ? process.env.NEXT_PUBLIC_CASH_STAR_CA_URL : process.env.NEXT_PUBLIC_CASH_STAR_US_URL

  const validateAllFields = (): { field: string; errorMessage: string }[] => {
    const errors: { field: string; errorMessage: string }[] = []

    const requiredFields: GiftCardFormField[] = [
      'selectedAmount',
      'recipientType',
      'senderName',
      'senderEmail',
      'message',
    ]

    if (isItForSomeoneElse) {
      requiredFields.push('recipientName', 'recipientEmail')
    }

    for (const fieldName of requiredFields) {
      const errorMessage = validateField(fieldName, formData[fieldName])
      if (errorMessage) {
        errors.push({ field: fieldName, errorMessage })
      }
    }

    return errors
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const errors = validateAllFields()
    setValidationErrors(errors)

    // If validation passes, proceed with form submission
    if (errors.length > 0) return

    // Call the onSubmit callback to close modal and show toast
    setTimeout(() => {
      onSubmit?.()
    }, 1000)

    if (formRef.current) formRef.current.submit()
  }

  return (
    <div className={styles.content}>
      <div className={styles.selectedCardContainer}>
        <h4>{i18n('selectedCard')}</h4>
        <div className={styles.cardImageContainer}>
          <SanityImage image={image} />
        </div>
        <p>{cardType === 'digital' ? i18n('digitalGiftCard') : i18n('physicalGiftCard')}</p>
      </div>

      <form
        ref={formRef}
        method="POST"
        name="giftCardForm"
        noValidate
        autoComplete="on"
        className={styles.formContainer}
        target="_blank"
        action={`${cashStarUrl}?locale=${currentLanguage || 'en-us'}`}
        encType="application/x-www-form-urlencoded"
        onSubmit={handleSubmit}
      >
        <h4>{i18n('selectedAmount')} *</h4>
        <FormField {...getFormFieldProps('selectedAmount')} />
        <h4>{i18n('whoIsThisFor')} *</h4>
        <FormField {...getFormFieldProps('recipientType')} />

        {isItForSomeoneElse && (
          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('recipientName')} />
            <FormField {...getFormFieldProps('recipientEmail')} />
          </div>
        )}
        <div className={styles.fieldsContainer}>
          <FormField {...getFormFieldProps('senderName')} />
          <FormField {...getFormFieldProps('senderEmail')} />
        </div>
        <div className={styles.fieldsContainer}>
          <FormField {...getFormFieldProps('message')} />
        </div>

        <input
          hidden
          name="amount"
          value={formData.selectedAmount.replace(i18n('currency'), '')}
          readOnly
        />
        <input
          hidden
          name="selfBuy"
          value={isItForMyself ? 'TRUE' : 'FALSE'}
          readOnly
        />
        <input
          hidden
          name="billingFirstName"
          value={formData.senderName}
          readOnly
        />
        <input
          hidden
          name="billingEmailAddress"
          value={formData.senderEmail}
          readOnly
        />
        <input
          hidden
          name="billingPurchaserEmail"
          value={formData.senderEmail}
          readOnly
        />
        <input
          hidden
          name="billingLastName"
          value=""
          readOnly
        />
        <input
          hidden
          name="currencyCode"
          value={i18n('currencyCode')}
          readOnly
        />
        <input
          hidden
          name="faceplateCode"
          value={faceplateCode}
          readOnly
        />
        <input
          hidden
          name="classicGift"
          value={IS_CLASSIC_GIFT_CARD ? 'TRUE' : 'FALSE'}
          readOnly
        />
        <input
          hidden
          name="cardType"
          value={cardType}
          readOnly
        />

        <Button
          type="submit"
          primary
          icon="caretRight"
          label={i18n('addToCart')}
          className={styles.submitButton}
        />
      </form>
    </div>
  )
}

GiftCardForm.displayName = 'GiftCardForm'

export default GiftCardForm
